import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "../context/AuthContext";
import { getCurrentClientEnvironment } from "../utils/clientEnvironment";
import { getTeamPresenceTabInstanceId } from "../utils/teamPresenceTelemetry";
import { resolveProductUsageContext } from "../utils/productUsageScreenKey";
import { postProductUsageEvents } from "../api/client";
import { getApiBaseURL } from "../api/apiBaseUrl";
import { readTabBearerToken } from "../utils/tabAuthStorage";

const FLUSH_INTERVAL_MS = 30_000;
const HEARTBEAT_INTERVAL_MS = 60_000;

function isBrowserTabFocused() {
  if (typeof document === "undefined") return false;
  return !document.hidden && document.visibilityState === "visible" && document.hasFocus();
}

function buildEvent(ctx, { eventType, durationSeconds, focusedSeconds, referrerScreenKey }) {
  if (!ctx) return null;
  const { deviceType, browserName, appVersion, appTheme } = getCurrentClientEnvironment();
  return {
    appSurface: ctx.appSurface,
    screenKey: ctx.screenKey,
    pathNorm: ctx.pathNorm,
    referrerScreenKey: referrerScreenKey || null,
    tabSessionId: getTeamPresenceTabInstanceId(),
    durationSeconds: Math.max(0, durationSeconds || 0),
    focusedSeconds: Math.max(0, focusedSeconds || 0),
    eventType,
    deviceType,
    browserName,
    appVersion,
    appTheme,
  };
}

function readBearerToken() {
  return readTabBearerToken();
}

function flushQueue(queueRef, { keepalive = false } = {}) {
  const batch = queueRef.current.splice(0, queueRef.current.length);
  if (!batch.length) return;

  const token = readBearerToken();
  if (!token) return;

  if (keepalive && typeof fetch !== "undefined") {
    fetch(`${getApiBaseURL()}/usage/product/events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Xpro-Client": "browser",
      },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    }).catch(() => {});
    return;
  }

  postProductUsageEvents(batch).catch(() => {});
}

/**
 * Rastreia navegação autenticada (tenant, console plataforma, portal parceiro).
 */
export default function ProductUsageTracker() {
  const { userData } = useAuthContext();
  const location = useLocation();
  const queueRef = useRef([]);
  const currentCtxRef = useRef(null);
  const referrerRef = useRef(null);
  const screenEnteredAtRef = useRef(Date.now());
  const focusedAccumRef = useRef(0);
  const lastTickRef = useRef(Date.now());
  const lastHeartbeatAtRef = useRef(0);

  const enqueue = (event) => {
    if (!event || userData?.id == null) return;
    queueRef.current.push(event);
  };

  const tickFocusedTime = () => {
    const now = Date.now();
    const deltaMs = Math.max(0, now - lastTickRef.current);
    lastTickRef.current = now;
    if (isBrowserTabFocused() && deltaMs > 0) {
      focusedAccumRef.current += deltaMs;
    }
    return now;
  };

  const durationOnCurrentScreen = () => {
    return Math.max(0, Math.round((Date.now() - screenEnteredAtRef.current) / 1000));
  };

  const emitSessionEndForCurrent = () => {
    const ctx = currentCtxRef.current;
    if (!ctx) return;
    tickFocusedTime();
    enqueue(
      buildEvent(ctx, {
        eventType: "session_end",
        durationSeconds: durationOnCurrentScreen(),
        focusedSeconds: Math.round(focusedAccumRef.current / 1000),
        referrerScreenKey: referrerRef.current,
      })
    );
  };

  const enterScreen = (fullPath, { flush = false } = {}) => {
    const ctx = resolveProductUsageContext(fullPath);
    if (!ctx) return;

    const prev = currentCtxRef.current;
    if (prev && prev.screenKey !== ctx.screenKey) {
      tickFocusedTime();
      const durationPrev = Math.max(0, Math.round((Date.now() - screenEnteredAtRef.current) / 1000));
      const focusedPrev = Math.round(focusedAccumRef.current / 1000);
      enqueue(
        buildEvent(ctx, {
          eventType: "page_view",
          durationSeconds: durationPrev,
          focusedSeconds: focusedPrev,
          referrerScreenKey: referrerRef.current,
        })
      );
      referrerRef.current = prev.screenKey;
      if (flush) flushQueue(queueRef);
    } else if (!prev) {
      enqueue(
        buildEvent(ctx, {
          eventType: "page_view",
          durationSeconds: 0,
          focusedSeconds: 0,
          referrerScreenKey: null,
        })
      );
    } else if (prev.screenKey === ctx.screenKey) {
      return;
    }

    currentCtxRef.current = ctx;
    screenEnteredAtRef.current = Date.now();
    focusedAccumRef.current = 0;
    lastTickRef.current = Date.now();
    lastHeartbeatAtRef.current = Date.now();
  };

  useEffect(() => {
    if (userData?.id == null) return undefined;
    enterScreen(`${location.pathname}${location.search || ""}`, { flush: true });
  }, [location.pathname, location.search, userData?.id]);

  useEffect(() => {
    if (userData?.id == null) return undefined;

    const flushTimer = setInterval(() => {
      tickFocusedTime();
      const ctx = currentCtxRef.current;
      const now = Date.now();
      if (ctx && now - lastHeartbeatAtRef.current >= HEARTBEAT_INTERVAL_MS && isBrowserTabFocused()) {
        lastHeartbeatAtRef.current = now;
        enqueue(
          buildEvent(ctx, {
            eventType: "heartbeat",
            durationSeconds: 60,
            focusedSeconds: 60,
            referrerScreenKey: referrerRef.current,
          })
        );
      }
      flushQueue(queueRef);
    }, FLUSH_INTERVAL_MS);

    const onHide = () => {
      emitSessionEndForCurrent();
      flushQueue(queueRef, { keepalive: true });
    };

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) onHide();
      else lastTickRef.current = Date.now();
    });
    window.addEventListener("beforeunload", onHide);

    return () => {
      clearInterval(flushTimer);
      window.removeEventListener("beforeunload", onHide);
    };
  }, [userData?.id]);

  return null;
}
